import { Button } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ActionEnum, getExpectNumber } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  ColumnInputRangeHeader,
  NumberFormat,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { TankEntity } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadFuels,
  loadStations,
  loadTanks,
  setAction,
  setSelectedTank,
  setShowSearchDrawer,
  setTankFilter,
} from '../../../stores/actions'
import {
  ManageTankModalComponent,
  PriceChangeHistoryModalComponent,
  SearchTankDrawerComponent,
} from './components'

export const TankPage: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { tankFilter, tanks, isTanksLoaded } = useSelector(
    (state: RootState) => state.tank
  )
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })
  const [tankLocal, setTankLocal] = React.useState<TankEntity | undefined>()

  const [filter, setFilter] = React.useState(tankFilter)

  React.useEffect(() => {
    setFilter(tankFilter)
  }, [tankFilter])

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
    // eslint-disable-next-line
  }, [isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadTanks())
    dispatch(loadStations())
    dispatch(loadFuels())
    // eslint-disable-next-line
  }, [])

  const filteredTanks = React.useMemo(() => {
    return tanks
      ? tanks.filter(
          (tank) =>
            tank.name
              .toLowerCase()
              .includes((tankFilter.name || '').toLowerCase()) &&
            (!tankFilter.currentPriceFrom ||
              tank.currentPrice >= tankFilter.currentPriceFrom) &&
            (!tankFilter.currentPriceTo ||
              tank.currentPrice <= tankFilter.currentPriceTo) &&
            (!tankFilter.volumeFrom || tank.volume >= tankFilter.volumeFrom) &&
            (!tankFilter.volumeTo || tank.volume <= tankFilter.volumeTo) &&
            (!tankFilter.remainFrom || tank.remain >= tankFilter.remainFrom) &&
            (!tankFilter.remainTo || tank.remain <= tankFilter.remainTo) &&
            tank.station.name
              .toLowerCase()
              .includes((tankFilter.stationName || '').toLowerCase()) &&
            tank.fuel.name
              .toLowerCase()
              .includes((tankFilter.fuelName || '').toLowerCase())
        )
      : []
  }, [tanks, tankFilter])

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: translate('tank.id', language),
        width: 75,
      },
      {
        dataIndex: 'name',
        title: (
          <ColumnInputHeader
            value={filter.name}
            titleKey="tank.name"
            onChange={(e) => setFilter({ ...filter, name: e.target.value })}
          />
        ),
        width: 100,
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="tank.volume"
            fromValue={filter.volumeFrom}
            toValue={filter.volumeTo}
            toKey="common.to"
            fromKey="common.from"
            onFromChange={(e: any) =>
              setFilter({
                ...filter,
                volumeFrom: e.target.value,
              })
            }
            onToChange={(e: any) =>
              setFilter({
                ...filter,
                volumeTo: e.target.value,
              })
            }
          />
        ),
        width: 175,
        render: (_: any, tank: TankEntity) => (
          <NumberFormat value={tank.volume} suffixKey="common.unitType" />
        ),
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="tank.remain"
            fromValue={filter.remainFrom}
            toValue={filter.remainTo}
            toKey="common.to"
            fromKey="common.from"
            onFromChange={(e: any) =>
              setFilter({
                ...filter,
                remainFrom: e.target.value,
              })
            }
            onToChange={(e: any) =>
              setFilter({
                ...filter,
                remainTo: e.target.value,
              })
            }
          />
        ),
        width: 175,
        render: (_: any, tank: TankEntity) => (
          <NumberFormat value={tank.remain} suffixKey="common.unitType" />
        ),
      },
      {
        dataIndex: 'currentPrice',
        title: (
          <ColumnInputRangeHeader
            titleKey="tank.currentPrice"
            fromValue={filter.currentPriceFrom}
            toValue={filter.currentPriceTo}
            toKey="common.to"
            fromKey="common.from"
            onFromChange={(e: any) =>
              setFilter({
                ...filter,
                currentPriceFrom: e.target.value,
              })
            }
            onToChange={(e: any) =>
              setFilter({
                ...filter,
                currentPriceTo: e.target.value,
              })
            }
          />
        ),
        width: 175,
        render: (_: any, tank: TankEntity) => (
          <NumberFormat
            suffixKey="common.moneyType"
            value={tank.currentPrice}
          />
        ),
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="tank.station"
            value={filter.stationName}
            onChange={(e) =>
              setFilter({
                ...filter,
                stationName: e.target.value,
              })
            }
          />
        ),
        render: (_: any, tank: TankEntity) => <span>{tank.station.name}</span>,
        width: 130,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="tank.fuel"
            value={filter.fuelName}
            onChange={(e) =>
              setFilter({
                ...filter,
                fuelName: e.target.value,
              })
            }
          />
        ),
        render: (_: any, tank: TankEntity) => <span>{tank.fuel.name}</span>,
        width: 100,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => {
              dispatch(
                setTankFilter({
                  ...filter,
                  volumeFrom: getExpectNumber(
                    true,
                    filter.volumeFrom,
                    filter.volumeTo
                  ),
                  volumeTo: getExpectNumber(
                    false,
                    filter.volumeFrom,
                    filter.volumeTo
                  ),
                  remainFrom: getExpectNumber(
                    true,
                    filter.remainFrom,
                    filter.remainTo
                  ),
                  remainTo: getExpectNumber(
                    false,
                    filter.remainFrom,
                    filter.remainTo
                  ),
                  currentPriceFrom: getExpectNumber(
                    true,
                    filter.currentPriceFrom,
                    filter.currentPriceTo
                  ),
                  currentPriceTo: getExpectNumber(
                    false,
                    filter.currentPriceFrom,
                    filter.currentPriceTo
                  ),
                })
              )
            }}
          />
        ),
        render: (_: any, tank: TankEntity) => (
          <>
            <TableActionColumnButtonComponent
              onViewButtonClick={() => {
                dispatch(setSelectedTank(tank))
                dispatch(setAction(ActionEnum.VIEW))
              }}
              onDeleteButtonClick={() => {
                dispatch(setSelectedTank(tank))
                dispatch(setAction(ActionEnum.DELETE))
              }}
            />
            <Button
              style={{
                margin: '2.5px',
              }}
              type="primary"
              onClick={() => setTankLocal(tank)}
              size="small"
            >
              {translate('tank.priceChangeHistory', language)}
            </Button>
          </>
        ),
        width: 130,
      },
    ],
    [dispatch, filter, language]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {tankLocal && (
        <PriceChangeHistoryModalComponent
          onClose={() => setTankLocal(undefined)}
          tank={tankLocal}
        />
      )}
      {management.showManageModal && <ManageTankModalComponent />}
      <PageTitleComponent
        hasSearchDrawer
        onCreateButtonClick={() => {
          dispatch(setSelectedTank())
          dispatch(setAction(ActionEnum.CREATE))
        }}
      />
      {management.showSearchDrawer && <SearchTankDrawerComponent />}
      <TableComponent
        data={filteredTanks}
        columns={columns}
        loading={!isTanksLoaded}
      />
    </div>
  )
}
